import { useCallback, useEffect, useRef, useState } from 'react';

import {
    CommunicationTemplateSource,
    isCommunicationsLayerBlock,
    isLaunchCommunicationBlock,
    isLegacyScheduleBlock,
    type CommunicationBlockDefinition,
    type CommunicationBlockIdValue,
} from './communicationBlocks';
import {
    cardKey,
    type CommunicationCardKey,
    type CommunicationCardState,
} from './communicationCardState';
import {
    resolveLegacyScheduleEmailContent,
    resolveLegacyScheduleEmailDefaults,
    resolveLegacyScheduleEmailEnabled,
} from './communicationsLegacyManagerEmailPersistence';
import {
    resolveCommunicationBlockEnabled,
    resolveCommunicationEmailContent,
} from './communicationsPersistence';
import { CommunicationEmailAudience } from './components/CommunicationEmailCard/CommunicationEmailCard';

import type { CommunicationEmailAudienceValue } from './components/CommunicationEmailCard/CommunicationEmailCard';
import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { useAmelioTranslations } from '@/hooks';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { EmailDefaults } from '@/utils/emailDefaults360';
import type { LocalizedText } from '@/utils/localization';
import type { Dispatch, SetStateAction } from 'react';
import type { Descendant } from 'slate';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    getEvaluation360EmailDefaults,
    getManagerNotificationEmailDefaults,
    getSelfEvaluationEmailDefaults,
} from '@/utils/emailDefaults';
import { getMinimalEmailDefaults } from '@/utils/emailDefaultsMinimal';

type LegacyScheduleQueryByEvaluationType = Partial<
    Record<EvaluationTypeValue, ReturnType<typeof useGetCycleEvaluation>>
>;

/**
 * A launch email is still SENT from its evaluation's own schedule when the communications block
 * carries nothing, so reading only the block would let this step's save bury the administrator's
 * own wording under a shipped default.
 */
const resolveCommunicationsLayerDefaults = ({
    block,
    audience,
    language,
    plainTextLanguage,
    shippedDefaults,
    legacyScheduleQueryByEvaluationType,
}: {
    block: CommunicationBlockDefinition;
    audience: CommunicationEmailAudienceValue;
    language: string;
    plainTextLanguage: string;
    shippedDefaults: EmailDefaults;
    legacyScheduleQueryByEvaluationType: LegacyScheduleQueryByEvaluationType;
}): EmailDefaults => {
    if (!isLaunchCommunicationBlock(block) || audience !== CommunicationEmailAudience.MANAGER) {
        return shippedDefaults;
    }

    return resolveLegacyScheduleEmailDefaults({
        cycleSchedule:
            legacyScheduleQueryByEvaluationType[block.evaluationType]?.data?.cycleSchedule,
        audience,
        language,
        plainTextLanguage,
        defaults: shippedDefaults,
    });
};

interface UseCommunicationCardsStateParams {
    group: CycleEvaluationGroup | undefined;
    visibleBlocks: CommunicationBlockDefinition[];
    isLegacyScheduleDataReady: boolean;
    legacyScheduleQueryByEvaluationType: LegacyScheduleQueryByEvaluationType;
    defaultLanguage: string;
    availableLanguageCodes: string[];
    currentUserEmail: string | undefined;
    t: ReturnType<typeof useAmelioTranslations>['t'];
}

export const useCommunicationCardsState = ({
    group,
    visibleBlocks,
    isLegacyScheduleDataReady,
    legacyScheduleQueryByEvaluationType,
    defaultLanguage,
    availableLanguageCodes,
    currentUserEmail,
    t,
}: UseCommunicationCardsStateParams) => {
    const [blockEnabled, setBlockEnabledState] = useState<
        Partial<Record<CommunicationBlockIdValue, boolean>>
    >({});
    const [cards, setCards] = useState<
        Partial<Record<CommunicationCardKey, CommunicationCardState>>
    >({});
    const [hasUnsavedSwitchChanges, setHasUnsavedSwitchChanges] = useState(false);

    // Latched by the first gesture that changes what would be SENT — a switch, a title, a message —
    // and never lowered. Switching a card's display language or typing a test address changes
    // nothing the server holds, so neither raises it.
    const [hasUserEdited, setHasUserEdited] = useState(false);

    const hydratedRef = useRef(false);

    // Hydration writes through the raw setter, never this one.
    const setBlockEnabled = useCallback<
        Dispatch<SetStateAction<Partial<Record<CommunicationBlockIdValue, boolean>>>>
    >((update) => {
        setHasUnsavedSwitchChanges(true);
        setHasUserEdited(true);
        setBlockEnabledState(update);
    }, []);

    const resolveDefaults = useCallback(
        ({
            block,
            audience,
            language,
        }: {
            block: CommunicationBlockDefinition;
            audience: CommunicationEmailAudienceValue;
            language: string;
        }): EmailDefaults => {
            if (
                block.templateSource === CommunicationTemplateSource.REUSED &&
                isLaunchCommunicationBlock(block)
            ) {
                if (audience === CommunicationEmailAudience.MANAGER) {
                    return getManagerNotificationEmailDefaults(language);
                }

                if (block.evaluationType === EvaluationType.SELF_EVALUATION) {
                    return getSelfEvaluationEmailDefaults(language);
                }

                if (block.evaluationType === EvaluationType.EVALUATION_360) {
                    return getEvaluation360EmailDefaults(language);
                }
            }

            return getMinimalEmailDefaults({
                language,
                cycleName: group?.name ?? '',
                milestoneTitle: t(block.labelKey, { lng: language }),
            });
        },
        [group?.name, t],
    );

    useEffect(() => {
        if (
            !group ||
            hydratedRef.current ||
            visibleBlocks.length === 0 ||
            !isLegacyScheduleDataReady
        ) {
            return;
        }

        hydratedRef.current = true;

        const language = defaultLanguage;
        const nextBlockEnabled: Partial<Record<CommunicationBlockIdValue, boolean>> = {};
        const nextCards: Partial<Record<CommunicationCardKey, CommunicationCardState>> = {};

        for (const block of visibleBlocks) {
            if (isLegacyScheduleBlock(block)) {
                const legacySchedule =
                    legacyScheduleQueryByEvaluationType[block.evaluationType]?.data?.cycleSchedule;

                const managerEnabled = resolveLegacyScheduleEmailEnabled(
                    legacySchedule,
                    CommunicationEmailAudience.MANAGER,
                );
                const employeeEnabled =
                    block.hasEmployeeCard &&
                    resolveLegacyScheduleEmailEnabled(
                        legacySchedule,
                        CommunicationEmailAudience.EMPLOYEE,
                    );

                // These two blocks carry no state of their own anywhere: their switch commands
                // their emails, so it is read back from them rather than stored.
                nextBlockEnabled[block.id] = managerEnabled || employeeEnabled;

                const managerTitleByLanguage: LocalizedText = {};
                const managerMessageByLanguage: Record<string, Descendant[]> = {};

                for (const lang of availableLanguageCodes) {
                    const managerDefaults = resolveDefaults({
                        block,
                        audience: CommunicationEmailAudience.MANAGER,
                        language: lang,
                    });
                    const managerContent = resolveLegacyScheduleEmailContent({
                        cycleSchedule: legacySchedule,
                        audience: CommunicationEmailAudience.MANAGER,
                        language: lang,
                        plainTextLanguage: defaultLanguage,
                        defaults: managerDefaults,
                    });

                    managerTitleByLanguage[lang] = managerContent.title;
                    managerMessageByLanguage[lang] = managerContent.message;
                }

                nextCards[cardKey(block.id, CommunicationEmailAudience.MANAGER)] = {
                    enabled: managerEnabled,
                    language,
                    titleByLanguage: managerTitleByLanguage,
                    messageByLanguage: managerMessageByLanguage,
                    customizedLanguages: new Set<string>(),
                    testEmailAddress: currentUserEmail ?? '',
                };

                if (block.hasEmployeeCard) {
                    const employeeTitleByLanguage: LocalizedText = {};
                    const employeeMessageByLanguage: Record<string, Descendant[]> = {};

                    for (const lang of availableLanguageCodes) {
                        const employeeDefaults = resolveDefaults({
                            block,
                            audience: CommunicationEmailAudience.EMPLOYEE,
                            language: lang,
                        });
                        const employeeContent = resolveLegacyScheduleEmailContent({
                            cycleSchedule: legacySchedule,
                            audience: CommunicationEmailAudience.EMPLOYEE,
                            language: lang,
                            plainTextLanguage: defaultLanguage,
                            defaults: employeeDefaults,
                        });

                        employeeTitleByLanguage[lang] = employeeContent.title;
                        employeeMessageByLanguage[lang] = employeeContent.message;
                    }

                    nextCards[cardKey(block.id, CommunicationEmailAudience.EMPLOYEE)] = {
                        enabled: employeeEnabled,
                        language,
                        titleByLanguage: employeeTitleByLanguage,
                        messageByLanguage: employeeMessageByLanguage,
                        customizedLanguages: new Set<string>(),
                        testEmailAddress: currentUserEmail ?? '',
                    };
                }

                continue;
            }

            if (!isCommunicationsLayerBlock(block)) {
                continue;
            }

            nextBlockEnabled[block.id] = resolveCommunicationBlockEnabled(
                group.communications,
                block.id,
            );

            const buildCard = (
                audience: CommunicationEmailAudienceValue,
            ): CommunicationCardState => {
                const titleByLanguage: LocalizedText = {};
                const messageByLanguage: Record<string, Descendant[]> = {};
                let enabled = true;

                for (const lang of availableLanguageCodes) {
                    const defaults = resolveCommunicationsLayerDefaults({
                        block,
                        audience,
                        language: lang,
                        plainTextLanguage: defaultLanguage,
                        shippedDefaults: resolveDefaults({ block, audience, language: lang }),
                        legacyScheduleQueryByEvaluationType,
                    });
                    const persisted = resolveCommunicationEmailContent({
                        communications: group.communications,
                        blockId: block.id,
                        audience,
                        language: lang,
                        defaults,
                    });

                    titleByLanguage[lang] = persisted.title;
                    messageByLanguage[lang] = persisted.message;
                    enabled = persisted.enabled;
                }

                return {
                    enabled,
                    language,
                    titleByLanguage,
                    messageByLanguage,
                    customizedLanguages: new Set<string>(),
                    testEmailAddress: currentUserEmail ?? '',
                };
            };

            nextCards[cardKey(block.id, CommunicationEmailAudience.MANAGER)] = buildCard(
                CommunicationEmailAudience.MANAGER,
            );

            if (block.hasEmployeeCard) {
                nextCards[cardKey(block.id, CommunicationEmailAudience.EMPLOYEE)] = buildCard(
                    CommunicationEmailAudience.EMPLOYEE,
                );
            }
        }

        setBlockEnabledState(nextBlockEnabled);
        setCards(nextCards);
    }, [group, visibleBlocks, isLegacyScheduleDataReady, legacyScheduleQueryByEvaluationType]);

    const updateCard = useCallback(
        (key: CommunicationCardKey, patch: Partial<CommunicationCardState>) => {
            if (patch.enabled !== undefined) {
                setHasUnsavedSwitchChanges(true);
                setHasUserEdited(true);
            }

            setCards((current) => {
                const existing = current[key];

                if (!existing) {
                    return current;
                }

                return { ...current, [key]: { ...existing, ...patch } };
            });
        },
        [],
    );

    const markSwitchesSaved = useCallback(() => setHasUnsavedSwitchChanges(false), []);

    const updateCardText = useCallback(
        (
            key: CommunicationCardKey,
            language: string,
            patch: { title?: string; message?: Descendant[] },
        ) => {
            setHasUserEdited(true);
            setCards((current) => {
                const existing = current[key];

                if (!existing) {
                    return current;
                }

                return {
                    ...current,
                    [key]: {
                        ...existing,
                        titleByLanguage:
                            patch.title === undefined
                                ? existing.titleByLanguage
                                : { ...existing.titleByLanguage, [language]: patch.title },
                        messageByLanguage:
                            patch.message === undefined
                                ? existing.messageByLanguage
                                : { ...existing.messageByLanguage, [language]: patch.message },
                        customizedLanguages: new Set(existing.customizedLanguages).add(language),
                    },
                };
            });
        },
        [],
    );

    return {
        blockEnabled,
        setBlockEnabled,
        cards,
        updateCard,
        updateCardText,
        hydratedRef,
        hasUnsavedSwitchChanges,
        hasUserEdited,
        markSwitchesSaved,
    };
};
